import styled from 'styled-components';
import { Button, Select, Alert } from '@mui/material';
import { colors } from '../../../../../themes';
import { hexWithAlpha } from '../../../../../utils';

export const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 24px;
  border-bottom: 1px solid ${hexWithAlpha('#000000', 0.05)};
  margin-bottom: 8px;
`;

export const DialogTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.default.text};
  margin: 0;
`;

export const DialogSubtitle = styled.p`
  color: ${hexWithAlpha(colors.default.text, 0.7)};
  margin: 8px 0 0 0;
  font-size: 16px;
`;

export const CloseButton = styled.button`
  padding: 8px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${hexWithAlpha('#000000', 0.04)};
  }
  
  svg {
    color: ${hexWithAlpha(colors.default.text, 0.6)};
  }
`;

export const ContentWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px 24px calc(8px + env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
`;

export const OriginalHoursBox = styled.div`
  background-color: ${hexWithAlpha(colors.default.primaryBlue, 0.08)};
  border: 1px solid ${hexWithAlpha(colors.default.primaryBlue, 0.16)};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${colors.default.primaryBlue};
  }
`;

export const OriginalHoursText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.default.primaryBlue};
  white-space: pre-line;
`;

export const Section = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.default.text};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 20px;
    height: 20px;
    color: ${hexWithAlpha(colors.default.text, 0.6)};
  }
`;

export const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.default.text};
  margin-bottom: 8px;
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  
  .MuiSelect-select {
    padding: 12px 16px;
    font-size: 18px;
    font-weight: 500;
  }
  
  .MuiOutlinedInput-notchedOutline {
    border-width: 2px;
    border-color: ${hexWithAlpha('#000000', 0.12)};
    border-radius: 12px;
  }
  
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${hexWithAlpha('#000000', 0.2)};
  }
  
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${colors.default.primaryBlue};
  }
`;

export const CalculationBox = styled.div`
  background-color: ${hexWithAlpha('#000000', 0.02)};
  border-radius: 12px;
  padding: 20px;
`;

export const CalculationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  
  &:not(:last-child) {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${hexWithAlpha('#000000', 0.05)};
  }
`;

export const HoursDisplay = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.default.text};
`;

export const MinimumText = styled.div`
  font-size: 13px;
  color: var(--primary-main, #FB7672);
  margin: 12px 0 0 0;
  text-align: center;
  font-weight: 500;
`;

export const EarningsBox = styled.div`
  background-color: var(--success-light, ${hexWithAlpha('#13CA82', 0.08)});
  border: 1px solid var(--success-border, ${hexWithAlpha('#13CA82', 0.2)});
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const EarningsLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 20px;
    height: 20px;
    color: var(--success-main, #13CA82);
  }
  
  span {
    font-weight: 600;
    font-size: 16px;
    color: ${colors.default.text};
  }
`;

export const EarningsAmount = styled.div`
  text-align: right;
`;

export const EarningsValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.default.text};
`;

export const EarningsDetail = styled.div`
  font-size: 12px;
  color: ${hexWithAlpha(colors.default.text, 0.6)};
  margin-top: 4px;
  line-height: 1.4;
`;

export const ProcessSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ProcessStep = styled.div`
  display: flex;
  gap: 12px;
`;

export const StepNumber = styled.div<{ variant?: 'blue' | 'green' }>`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background-color: var(--primary-blue, #0B4598);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(11, 69, 152, 0.15);
`;

export const StepText = styled.p`
  color: ${hexWithAlpha(colors.default.text, 0.6)};
  font-size: 14px;
  margin: 0;
  padding-top: 4px;
  
  strong {
    font-weight: 500;
    color: ${colors.default.text};
  }
`;

export const CommitmentBox = styled(Alert)`
  background-color: ${hexWithAlpha('#FF6B35', 0.08)};
  border: 1px solid ${hexWithAlpha('#FF6B35', 0.16)};
  border-radius: 12px;
  padding: 16px;
  
  .MuiAlert-icon {
    color: #FF6B35;
    padding: 0;
    margin-right: 12px;
  }
  
  .MuiAlert-message {
    color: ${colors.default.text};
    padding: 0;
  }
`;

export const WarningIcon = styled.div`
  color: #FF6B35;
  display: flex;
  align-items: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

export const CommitmentContent = styled.div``;

export const CommitmentTitle = styled.h4`
  font-weight: 600;
  color: ${colors.default.text};
  margin: 0 0 4px 0;
`;

export const CommitmentText = styled.p`
  font-size: 14px;
  color: ${hexWithAlpha(colors.default.text, 0.6)};
  margin: 0;
  
  strong {
    font-weight: 600;
    color: ${colors.default.text};
  }
`;

export const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: ${hexWithAlpha(colors.default.primaryBlue, 0.04)};
  border: 1px solid ${hexWithAlpha(colors.default.primaryBlue, 0.12)};
  border-radius: 12px;
`;

export const CheckboxText = styled.label`
  font-size: 14px;
  color: ${colors.default.text};
  cursor: pointer;
  padding-top: 2px;
  
  .status {
    font-weight: 600;
    color: ${colors.default.primaryBlue};
  }
`;

export const ButtonContainer = styled.div`
  margin-top: 32px;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SubmitButton = styled(Button)`
  width: 100%;
  background-color: ${colors.default.primaryBlue};
  color: white;
  font-weight: 600;
  padding: 16px 24px;
  border-radius: 12px;
  text-transform: none;
  font-size: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background-color: ${hexWithAlpha(colors.default.primaryBlue, 0.9)};
  }
  
  &:disabled {
    background-color: ${hexWithAlpha('#000000', 0.12)};
    color: ${hexWithAlpha('#000000', 0.26)};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const CancelButton = styled(Button)`
  width: 100%;
  background-color: white;
  color: ${hexWithAlpha(colors.default.text, 0.7)};
  font-weight: 600;
  padding: 16px 24px;
  border-radius: 12px;
  text-transform: none;
  font-size: 16px;
  border: 2px solid ${hexWithAlpha('#000000', 0.12)};
  
  &:hover {
    background-color: ${hexWithAlpha('#000000', 0.02)};
    border-color: ${hexWithAlpha('#000000', 0.2)};
  }
`;

export const Disclaimer = styled.p`
  font-size: 12px;
  color: ${hexWithAlpha(colors.default.text, 0.5)};
  text-align: center;
  margin: 16px 0 0 0;
`;

export const PartialOfferCheckbox = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  background-color: ${hexWithAlpha('#000000', 0.02)};
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${hexWithAlpha('#000000', 0.04)};
  }
`;

export const PartialOfferCheckboxLabel = styled.label`
  font-size: 14px;
  color: ${colors.default.text};
  cursor: pointer;
  user-select: none;
  margin-left: 8px;
  white-space: pre-line;
`;

export const GHRBonusCallout = styled.div`
  background-color: ${hexWithAlpha('#13CA82', 0.08)};
  border: 1px solid ${hexWithAlpha('#13CA82', 0.2)};
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 20px;
    height: 20px;
    color: #13CA82;
    flex-shrink: 0;
  }
`;

export const GHRBonusText = styled.div`
  font-size: 14px;
  color: ${colors.default.text};
  line-height: 1.4;
  
  .amount {
    font-weight: 700;
    color: #13CA82;
  }
  
  .date {
    font-weight: 600;
  }
`;

export const SelectedRateDisplay = styled.div`
  background-color: ${hexWithAlpha(colors.default.primaryBlue, 0.04)};
  border: 2px solid ${colors.default.primaryBlue};
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 12px;
  text-align: center;
  
  .label {
    font-size: 12px;
    color: ${hexWithAlpha(colors.default.text, 0.6)};
    margin-bottom: 4px;
  }
  
  .rate {
    font-size: 24px;
    font-weight: 700;
    color: ${colors.default.text};
  }
  
  .bonus {
    font-size: 18px;
    font-weight: 700;
    color: #13CA82;
    margin-left: 4px;
  }
`;

export const AcceptanceFeedback = styled.div<{ $feedbackColor: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0 2px;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ $feedbackColor }) => $feedbackColor};
    flex-shrink: 0;
  }

  span {
    font-size: 13px;
    font-style: italic;
    color: ${({ $feedbackColor }) => $feedbackColor};
    line-height: 1.4;
  }
`;

export const StatusUnlockBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  background-color: ${hexWithAlpha('#13CA82', 0.08)};
  border: 1px solid ${hexWithAlpha('#13CA82', 0.2)};
  border-radius: 12px;

  svg {
    width: 20px;
    height: 20px;
    color: #13CA82;
    flex-shrink: 0;
  }
`;

export const StatusUnlockText = styled.div`
  font-size: 14px;
  color: ${colors.default.text};
  line-height: 1.4;

  strong {
    font-weight: 700;
    color: #13CA82;
  }

  .bonus {
    font-size: 12px;
    color: ${hexWithAlpha(colors.default.text, 0.6)};
  }
`;

export const StatusBadge = styled.span<{ $variant?: 'pro' | 'elite' }>`
  font-weight: 700;
  color: ${({ $variant }) => $variant === 'elite' ? 'var(--primary-blue, #0B4598)' : '#13CA82'};
`;

export const StatusUpgradeHint = styled.div`
  font-size: 13px;
  color: ${hexWithAlpha(colors.default.text, 0.5)};
  margin-top: 8px;
  text-align: center;
  line-height: 1.5;

  a {
    color: var(--primary-blue, #0B4598);
    text-decoration: underline;
    font-weight: 500;
    margin-left: 4px;
  }
`;

export const CompetingOffersInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  margin-top: 12px;
  background: ${hexWithAlpha(colors.default.primaryBlue, 0.05)};
  border: 1px solid ${hexWithAlpha(colors.default.primaryBlue, 0.12)};
  border-radius: 8px;

  svg {
    width: 18px;
    height: 18px;
    color: ${hexWithAlpha(colors.default.primaryBlue, 0.5)};
    flex-shrink: 0;
    margin-top: 1px;
  }

  span {
    font-size: 13px;
    color: ${hexWithAlpha(colors.default.text, 0.65)};
    line-height: 1.5;

    strong {
      font-weight: 600;
      color: ${colors.default.primaryBlue};
    }
  }
`;